import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CLOSE_CODES, LOBBY_CLOSED_MESSAGE, PROTOCOL_VERSION } from '../domain/constants';

// Connessione all'asta.
//
// Differenze rispetto alla versione precedente: lo schema e wss:// quando la
// pagina e servita in HTTPS (prima ws:// era cablato e il browser bloccava il
// mixed content, quindi la modalita live non funzionava in produzione); il
// socket vive in una ref invece che in useState; i comandi inviati prima
// dell'apertura vengono accodati invece di sollevare InvalidStateError; e la
// riconnessione avviene con backoff esponenziale.

// 4003 = sessione ripresa da un'altra scheda: riconnettersi qui farebbe
// rimbalzare le due schede all'infinito. 4402 = la lobby non esiste piu,
// quindi ritentare produrrebbe solo un 4400 dopo l'altro.
const TERMINAL_CODES = new Set([1000, ...Object.values(CLOSE_CODES)]);
const MAX_BACKOFF = 8000;

function socketUrl(lobbyName, token) {
    const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const path = `/ws/auctions/${encodeURIComponent(lobbyName)}`;
    return `${scheme}//${window.location.host}${path}?token=${encodeURIComponent(token)}`;
}

export default function useAuctionSocket(lobbyName, token) {
    const [auction, setAuction] = useState(null);
    const [participantId, setParticipantId] = useState(null);
    const [connection, setConnection] = useState({
        connected: false,
        reconnecting: false,
        closedReason: null,
        closedCode: null,
    });
    const [deadline, setDeadline] = useState(null);
    const [messages, setMessages] = useState([]);
    const [staleProtocol, setStaleProtocol] = useState(false);

    const socketRef = useRef(null);
    const queueRef = useRef([]);
    const attemptRef = useRef(0);
    const retryRef = useRef(null);
    const seqRef = useRef(-1);
    const closedRef = useRef(false);

    const pushMessage = useCallback((message) => {
        setMessages((current) => [
            ...current.slice(-4),
            { id: `${Date.now()}-${Math.random()}`, ...message },
        ]);
    }, []);

    const dismissMessage = useCallback((id) => {
        setMessages((current) => current.filter((message) => message.id !== id));
    }, []);

    useEffect(() => {
        if (!lobbyName || !token) return undefined;

        closedRef.current = false;
        let disposed = false;

        const connect = () => {
            if (disposed) return;
            const socket = new WebSocket(socketUrl(lobbyName, token));
            socketRef.current = socket;

            socket.onopen = () => {
                attemptRef.current = 0;
                setConnection({
                    connected: true,
                    reconnecting: false,
                    closedReason: null,
                    closedCode: null,
                });
                const queued = queueRef.current;
                queueRef.current = [];
                queued.forEach((payload) => socket.send(payload));
            };

            socket.onmessage = (event) => {
                let data;
                try {
                    data = JSON.parse(event.data);
                } catch {
                    return;
                }
                handleEvent(data);
            };

            socket.onclose = (event) => {
                if (disposed) return;
                setConnection((current) => ({ ...current, connected: false }));

                if (TERMINAL_CODES.has(event.code)) {
                    closedRef.current = true;
                    setConnection({
                        connected: false,
                        reconnecting: false,
                        closedReason: closeReason(event.code),
                        closedCode: event.code,
                    });
                    return;
                }

                // Codici non definitivi (rete caduta, server riavviato): si
                // riprova con attese crescenti invece di congelare la pagina.
                attemptRef.current += 1;
                const delay = Math.min(MAX_BACKOFF, 500 * 2 ** (attemptRef.current - 1));
                setConnection({
                    connected: false,
                    reconnecting: true,
                    closedReason: null,
                    closedCode: null,
                });
                retryRef.current = setTimeout(connect, delay + Math.random() * 250);
            };
        };

        const handleEvent = (data) => {
            if (data.protocol_version && data.protocol_version !== PROTOCOL_VERSION) {
                setStaleProtocol(true);
                return;
            }

            switch (data.type) {
                case 'welcome':
                    setParticipantId(data.payload.participant_id);
                    break;
                case 'state': {
                    // Uno snapshot piu vecchio di quello gia applicato si scarta.
                    if (data.seq < seqRef.current) return;
                    seqRef.current = data.seq;
                    setAuction(data.payload);
                    setDeadline(data.payload.lot?.deadline_ms ?? null);
                    break;
                }
                case 'timer':
                    setDeadline(data.payload.deadline_ms);
                    break;
                case 'error':
                    pushMessage({ level: 'error', text: data.payload.message });
                    break;
                case 'notice':
                    pushMessage({ level: data.payload.level ?? 'info', text: data.payload.message });
                    break;
                case 'released':
                    pushMessage({
                        level: 'success',
                        text: `Giocatore svincolato: +${data.payload.refund} crediti.`,
                    });
                    break;
                default:
                    break;
            }
        };

        connect();

        return () => {
            disposed = true;
            clearTimeout(retryRef.current);
            const socket = socketRef.current;
            socketRef.current = null;
            if (socket && socket.readyState <= WebSocket.OPEN) socket.close(1000, 'uscita');
        };
    }, [lobbyName, token, pushMessage]);

    const send = useCallback((message) => {
        const payload = JSON.stringify(message);
        const socket = socketRef.current;
        if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(payload);
        } else if (!closedRef.current) {
            // Accodato finche la connessione non e pronta.
            queueRef.current.push(payload);
        }
    }, []);

    const remainingSeconds = useCountdown(deadline);

    return useMemo(
        () => ({
            auction,
            participantId,
            connection,
            remainingSeconds,
            messages,
            dismissMessage,
            staleProtocol,
            send,
        }),
        [
            auction,
            participantId,
            connection,
            remainingSeconds,
            messages,
            dismissMessage,
            staleProtocol,
            send,
        ],
    );
}

/** Conto alla rovescia locale: il server manda la scadenza, non un tick al secondo. */
function useCountdown(deadlineMs) {
    const [remaining, setRemaining] = useState(0);

    useEffect(() => {
        if (!deadlineMs) {
            setRemaining(0);
            return undefined;
        }
        const tick = () => {
            setRemaining(Math.max(0, Math.ceil((deadlineMs - Date.now()) / 1000)));
        };
        tick();
        const handle = setInterval(tick, 250);
        return () => clearInterval(handle);
    }, [deadlineMs]);

    return remaining;
}

function closeReason(code) {
    switch (code) {
        case CLOSE_CODES.LOBBY_NOT_FOUND:
            return "Questa asta non esiste piu.";
        case CLOSE_CODES.BAD_TOKEN:
            return 'La tua sessione non e piu valida. Rientra nell’asta.';
        case CLOSE_CODES.REMOVED:
            return 'Sei stato rimosso dall’asta.';
        case CLOSE_CODES.REPLACED:
            return 'Hai ripreso questa asta in un’altra scheda o su un altro dispositivo.';
        case CLOSE_CODES.LOBBY_CLOSED:
            return LOBBY_CLOSED_MESSAGE;
        default:
            return 'Connessione chiusa.';
    }
}
